import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { toast } from "sonner";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";
import { reportError } from "../utils/errorReporting";
import { resolveErrorMessage } from "../utils/errorResolver";
import { createAuthSessionRecovery } from "../utils/authRecovery";
import i18n from "../i18n";

// Single recovery path for credential failures (expired/revoked session):
// any query 401 goes through one forced token refresh; if that heals the
// session every query refetches, otherwise the user is signed out into the
// login redirect. One shot per signed-in session.
const authRecovery = createAuthSessionRecovery({
  refreshToken: () => {
    const user = auth.currentUser;
    return user
      ? user.getIdToken(true)
      : Promise.reject(new Error("No signed-in user to refresh"));
  },
  onRecovered: async () => {
    // Initial fetches (no cached data) dedupe onto their in-flight
    // stale-token request instead of restarting on invalidate — cancel them
    // first so everything refetches with the fresh token.
    await queryClient.cancelQueries();
    await queryClient.invalidateQueries();
  },
  signOut: () => void signOut(auth),
  hasSession: () => auth.currentUser !== null,
});

// This module outlives SPA sign-out/sign-in cycles; a new auth session must
// start with a clean recovery slate instead of inheriting a spent one.
// Every cached query row is scoped to the signed-in user (settings, projects,
// workflows all resolve auth.uid server-side), so when the identity actually
// changes the whole cache is the previous user's data — drop it rather than
// let staleTime serve user A's rows to user B (or let an optimistic update
// copy them into B's mutation payload).
let lastAuthUid: string | null | undefined;
onAuthStateChanged(auth, (user) => {
  authRecovery.reset();
  const uid = user?.uid ?? null;
  if (lastAuthUid !== undefined && lastAuthUid !== uid) {
    // Cancel first so an in-flight fetch issued under the previous account's
    // token can't settle after the removal and repopulate the fresh cache
    // with the previous user's rows.
    void queryClient.cancelQueries().then(() => queryClient.removeQueries());
  }
  lastAuthUid = uid;
});

export const queryClient = new QueryClient({
  // TanStack's onlineManager only learns about connectivity from window
  // online/offline events; after an offline event the default networkMode
  // ("online") pauses queries silently — no fetch, no error — which the app's
  // loading gates read as "loading" forever and the dashboard as "no
  // projects". "always" keeps firing requests so a real outage fails into
  // isError and reaches the error/retry screens. Same for mutations, whose
  // paused state would silently queue persistence writes.
  defaultOptions: {
    queries: { networkMode: "always" },
    mutations: { networkMode: "always" },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      authRecovery.onError(error);

      // Only report if the query has no more retries
      if (
        query.state.fetchStatus === "fetching" ||
        query.state.fetchFailureCount >= 1
      ) {
        reportError(error, {
          queryKey: query.queryKey,
          type: "query_error",
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      reportError(error, {
        mutationKey: mutation.options.mutationKey,
        type: "mutation_error",
      });

      // Mutations with suppressToast handle errors inline (e.g. login form)
      if (mutation.meta?.suppressToast) return;

      const message = resolveErrorMessage(error, i18n.t.bind(i18n));
      toast.error(message);
    },
  }),
});
